import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, EMPTY } from 'rxjs';
import { map, expand, reduce } from 'rxjs/operators';
import { ApiService } from './api.service';
import { ResponsePaginatedWrapper, ResponseSingleWrapper } from '@/app/shared/dto';

// Shipment Report DTOs
export interface DocumentStats {
    id: string;
    type: string;
    filename: string;
    parseStatus: string | null;
    confidence: number | null;
    uploadedAt: string | null;
    parsedAt: string | null;
    processingTimeSeconds: number | null;
}

export interface ShipmentReportItem {
    id: string;
    shipmentRef: string | null;
    projectId: string | null;
    projectTitle: string | null;
    projectStatus: string | null;
    projectDueDate: string | null;
    customer: string | null;
    customerName: string | null;
    productId: string | null;
    productNameAndSize: string | null;
    fabricator: string | null;
    receivingDate: string | null;
    receivingNumber: string | null;
    warehouse: string | null;
    isDirectShipment: boolean | null;
    regulatoryScope: string | null;
    isDraft: boolean | null;
    progressPercent: number | null;
    validationErrorsCount: number | null;
    validationWarningsCount: number | null;
    lastValidatedAt: string | null;
    documentProvisions: DocumentProvisions | null;
    documents?: DocumentStats[];
    assignees: ShipmentAssignee[];
    createdAt: string;
    updatedAt: string;
}

export interface DocumentProvisionItem {
    provided?: boolean;
    required?: boolean;
}

export interface DocumentProvisions {
    // New structure from database
    batch_certificate?: DocumentProvisionItem;
    certificate_of_analysis?: DocumentProvisionItem;
    receiving_inspection_form?: DocumentProvisionItem;
    health_group_release_letter?: DocumentProvisionItem;
    certificate_of_compliance?: DocumentProvisionItem;
    certificate_of_manufacture?: DocumentProvisionItem;
    executed_batch_record?: DocumentProvisionItem;
    // Legacy structure support
    fab_coa_status?: string;
    fab_coc_status?: string;
    fab_com_status?: string;
    batch_certificate_status?: string;
    executed_batch_record_status?: string;
    hc_group2_status?: string;
    hc_group3_status?: string;
    hc_group4_status?: string;
    fab_coa_document_id?: string | null;
    fab_coc_document_id?: string | null;
    fab_com_document_id?: string | null;
    batch_certificate_document_id?: string | null;
    executed_batch_record_document_id?: string | null;
    hc_group2_document_id?: string | null;
    hc_group3_document_id?: string | null;
    hc_group4_document_id?: string | null;
}

export interface ShipmentAssignee {
    id: string;
    userId: string;
    role: string;
    userName?: string;
    userEmail?: string;
}

export interface ShipmentDocument {
    id: string;
    shipmentId: string;
    type: string;
    filename: string;
    mimeType: string | null;
    storageUri: string | null;
    sha256: string | null;
    uploadedAt: string | null;
    parsedAt: string | null;
    parseStatus: string | null;
    mlJobId: string | null;
    mlErrorMessage: string | null;
    confidence: number | null;
    isAccepted: boolean | null;
    createdAt: string;
    updatedAt: string;
}

export interface DocumentCompletenessItem {
    shipmentId: string;
    shipmentRef: string | null;
    customerName: string | null;
    documents: ShipmentDocument[];
    documentProvisions: DocumentProvisions | null;
    requiredDocuments: string[];
    uploadedDocuments: string[];
    missingDocuments: string[];
    completenessPercent: number;
}

export interface DispositionRecord {
    shipmentId: string;
    isPrevReviewed: boolean | null;
    previousReviewReference: string | null;
    fabricatorDeviationNumber: string | null;
    fabricatorReworkNumber: string | null;
    isFabCoaAcceptable: boolean | null;
    isFabCocAcceptable: boolean | null;
    isBatchCertificateAcceptable: boolean | null;
    isExecutedBatchRecordAcceptable: boolean | null;
    areWarehouseReportsAcceptable: boolean | null;
    areTempDataLoggersAcceptable: boolean | null;
    isExemptFromTesting: boolean | null;
    isFullTestingRequired: boolean | null;
    isAnnualConfirmatoryRequired: boolean | null;
    isIdentityTestingRequired: boolean | null;
    isPackagingInspectionAcceptable: boolean | null;
    finalDisposition?: FinalDisposition;
    createdAt: string;
    updatedAt: string;
}

export interface FinalDisposition {
    status: string | null;
    qtyValue: number | null;
    qtyUnit: string | null;
    internalDeviationNumber: string | null;
    testingLabDeviationNumber: string | null;
    commentsRaw: string | null;
    totalUnitsReceived: number | null;
    unitsForInspection: number | null;
    unitsForRetains: number | null;
}

export interface ShipmentTimelineEvent {
    id: string;
    createdAt: string;
    actorUserId: string | null;
    actorType: string;
    actorUser?: {
        email: string;
        role: string;
    };
    subjectType: string;
    subjectId: string;
    action: string;
    outcome: string;
    ipAddress: string | null;
    userAgent?: {
        parsedUserAgent: string;
    };
    requestId: string | null;
    correlationId: string | null;
    details: Record<string, any> | null;
}

export interface ShipmentReportFilters {
    searchTerm?: string;
    projectStatus?: string;
    customerId?: string;
    productId?: string;
    assigneeId?: string;
    regulatoryScope?: string;
    progressMin?: number;
    progressMax?: number;
    hasValidationErrors?: boolean;
    dateFrom?: string;
    dateTo?: string;
    dateType?: 'created' | 'receiving' | 'updated';
}

@Injectable({
    providedIn: 'root',
})
export class ReportsService extends ApiService {
    protected get basePath(): string {
        return '/shipments';
    }

    private fetchAllPages<T>(url: string, params?: HttpParams): Observable<T[]> {
        const getPage = (pageNumber: number): Observable<ResponsePaginatedWrapper<T>> => {
            let httpParams = params || new HttpParams();
            httpParams = httpParams.set('page', pageNumber.toString()).set('limit', '100');
            return this.http.get<ResponsePaginatedWrapper<T>>(url, { params: httpParams, withCredentials: true });
        };

        return getPage(1).pipe(
            expand((response) => {
                if (response.meta && response.meta.currentPage < response.meta.totalPages) {
                    return getPage(response.meta.currentPage + 1);
                }
                return EMPTY;
            }),
            map((response) => response.data || []),
            reduce((acc, data) => acc.concat(data), [] as T[]),
        );
    }

    // ==================== SHIPMENT REPORTS ====================

    /**
     * Get all shipments for the status dashboard report
     */
    getShipmentStatusDashboard(filters?: ShipmentReportFilters): Observable<ShipmentReportItem[]> {
        let params = new HttpParams();
        
        if (filters?.searchTerm) {
            params = params.set('search', filters.searchTerm);
        }
        if (filters?.projectStatus) {
            params = params.set('projectStatus', filters.projectStatus);
        }
        if (filters?.customerId) {
            params = params.set('customer', filters.customerId);
        }

        return this.http.get<ShipmentReportItem[]>(`${this.apiUrl}/shipments`, { params, withCredentials: true }).pipe(
            map((response) => {
                // Handle both array response and { data: [...] } response formats
                const data = Array.isArray(response) ? response : (response as any).data || [];
                return data.map((item: any) => this.normalizeShipmentReport(item));
            }),
        );
    }

    /**
     * Get document completeness report for all shipments
     */
    getDocumentCompletenessReport(filters?: ShipmentReportFilters): Observable<DocumentCompletenessItem[]> {
        return this.getShipmentStatusDashboard(filters).pipe(
            map((shipments) => shipments.map((shipment) => this.calculateDocumentCompleteness(shipment))),
        );
    }

    /**
     * Get disposition record for a specific shipment
     */
    getDispositionRecord(shipmentId: string): Observable<DispositionRecord | null> {
        return this.http.get<{ data: any }>(`${this.apiUrl}/shipments/${shipmentId}/disposition`, { withCredentials: true }).pipe(
            map((response) => {
                // Return null if no data or no quality review exists
                if (!response.data || !response.data.qualityReview) {
                    return null;
                }
                return this.normalizeDispositionRecord(response.data);
            }),
        );
    }

    /**
     * Get timeline/audit trail for a specific shipment
     */
    getShipmentTimeline(shipmentId: string, limit: number = 50): Observable<ShipmentTimelineEvent[]> {
        const params = new HttpParams()
            .set('subjectType', 'shipment')
            .set('subjectId', shipmentId)
            .set('limit', limit.toString());

        return this.http.get<{ data: ShipmentTimelineEvent[] }>(`${this.apiUrl}/audit-logs`, { params, withCredentials: true }).pipe(
            map((response) => (response.data || []).map((event) => this.normalizeTimelineEvent(event))),
        );
    }

    /**
     * Get documents for a specific shipment
     */
    getShipmentDocuments(shipmentId: string): Observable<ShipmentDocument[]> {
        return this.http.get<{ data: any }>(`${this.apiUrl}/shipments/${shipmentId}`, { withCredentials: true }).pipe(
            map((response) => {
                const shipment = response.data;
                return (shipment?.documents || []).map((doc: any) => this.normalizeDocument(doc));
            }),
        );
    }

    /**
     * Export shipment report data to CSV format
     */
    exportShipmentsToCsv(shipments: ShipmentReportItem[]): string {
        const headers = [
            'Shipment Ref',
            'Project ID',
            'Project Title',
            'Status',
            'Customer',
            'Product',
            'Fabricator',
            'Regulatory Scope',
            'Progress %',
            'Validation Errors',
            'Validation Warnings',
            'Receiving Date',
            'Warehouse',
            'Created At',
            'Updated At',
        ];

        const rows = shipments.map((s) => [
            s.shipmentRef || '',
            s.projectId || '',
            s.projectTitle || '',
            s.projectStatus || '',
            s.customerName || '',
            s.productNameAndSize || '',
            s.fabricator || '',
            s.regulatoryScope || '',
            s.progressPercent?.toString() || '0',
            s.validationErrorsCount?.toString() || '0',
            s.validationWarningsCount?.toString() || '0',
            s.receivingDate || '',
            s.warehouse || '',
            s.createdAt || '',
            s.updatedAt || '',
        ]);

        return this.arrayToCsv([headers, ...rows]);
    }

    /**
     * Export document completeness report to CSV
     */
    exportDocumentCompletenessToCsv(items: DocumentCompletenessItem[]): string {
        const headers = [
            'Shipment Ref',
            'Customer',
            'Completeness %',
            'Required Documents',
            'Uploaded Documents',
            'Missing Documents',
        ];

        const rows = items.map((item) => [
            item.shipmentRef || '',
            item.customerName || '',
            item.completenessPercent.toString(),
            item.requiredDocuments.join('; '),
            item.uploadedDocuments.join('; '),
            item.missingDocuments.join('; '),
        ]);

        return this.arrayToCsv([headers, ...rows]);
    }

    // ==================== HELPER METHODS ====================

    private normalizeShipmentReport(item: any): ShipmentReportItem {
        return {
            ...item,
            createdAt: item.createdAt ? new Date(item.createdAt).toISOString() : '',
            updatedAt: item.updatedAt ? new Date(item.updatedAt).toISOString() : '',
            assignees: item.assignees || [],
        };
    }

    private normalizeDispositionRecord(record: any): DispositionRecord {
        // The backend now returns qualityReview and finalDisposition as nested objects
        const qr = record.qualityReview || {};
        const fd = record.finalDisposition;
        
        return {
            shipmentId: qr.shipmentId || record.projectDetails?.projectId || '',
            isPrevReviewed: qr.isPrevReviewed ?? null,
            previousReviewReference: qr.previousReviewReference ?? null,
            fabricatorDeviationNumber: qr.fabricatorDeviationNumber ?? null,
            fabricatorReworkNumber: qr.fabricatorReworkNumber ?? null,
            isFabCoaAcceptable: qr.isFabCoaAcceptable ?? null,
            isFabCocAcceptable: qr.isFabCocAcceptable ?? null,
            isBatchCertificateAcceptable: qr.isBatchCertificateAcceptable ?? null,
            isExecutedBatchRecordAcceptable: qr.isExecutedBatchRecordAcceptable ?? null,
            areWarehouseReportsAcceptable: qr.areWarehouseReportsAcceptable ?? null,
            areTempDataLoggersAcceptable: qr.areTempDataLoggersAcceptable ?? null,
            isExemptFromTesting: qr.isExemptFromTesting ?? null,
            isFullTestingRequired: qr.isFullTestingRequired ?? null,
            isAnnualConfirmatoryRequired: qr.isAnnualConfirmatoryRequired ?? null,
            isIdentityTestingRequired: qr.isIdentityTestingRequired ?? null,
            isPackagingInspectionAcceptable: qr.isPackagingInspectionAcceptable ?? null,
            finalDisposition: fd ? {
                status: fd.status ?? null,
                qtyValue: fd.qtyValue ?? null,
                qtyUnit: fd.qtyUnit ?? null,
                internalDeviationNumber: fd.internalDeviationNumber ?? null,
                testingLabDeviationNumber: fd.testingLabDeviationNumber ?? null,
                commentsRaw: fd.commentsRaw ?? null,
                totalUnitsReceived: fd.totalUnitsReceived ?? null,
                unitsForInspection: fd.unitsForInspection ?? null,
                unitsForRetains: fd.unitsForRetains ?? null,
            } : undefined,
            createdAt: qr.createdAt ? new Date(qr.createdAt).toISOString() : '',
            updatedAt: qr.updatedAt ? new Date(qr.updatedAt).toISOString() : '',
        };
    }

    private normalizeTimelineEvent(event: any): ShipmentTimelineEvent {
        return {
            ...event,
            createdAt: event.createdAt ? new Date(event.createdAt).toISOString() : '',
        };
    }

    private normalizeDocument(doc: any): ShipmentDocument {
        return {
            ...doc,
            createdAt: doc.createdAt ? new Date(doc.createdAt).toISOString() : '',
            updatedAt: doc.updatedAt ? new Date(doc.updatedAt).toISOString() : '',
        };
    }

    private calculateDocumentCompleteness(shipment: ShipmentReportItem): DocumentCompletenessItem {
        const provisions = shipment.documentProvisions || {};
        
        // Determine required documents based on provisions
        const requiredDocuments: string[] = [];
        const uploadedDocuments: string[] = [];

        // New structure document types
        const newStructureDocTypes = [
            { key: 'certificate_of_analysis', label: 'Certificate of Analysis (CoA)' },
            { key: 'certificate_of_compliance', label: 'Certificate of Compliance (CoC)' },
            { key: 'certificate_of_manufacture', label: 'Certificate of Manufacture (CoM)' },
            { key: 'batch_certificate', label: 'Batch Certificate' },
            { key: 'executed_batch_record', label: 'Executed Batch Record' },
            { key: 'receiving_inspection_form', label: 'Receiving Inspection Form' },
            { key: 'health_group_release_letter', label: 'Health Group Release Letter' },
        ];

        // Check if using new structure (has nested objects with required/provided)
        const isNewStructure = Object.values(provisions).some(
            (v) => v && typeof v === 'object' && ('required' in v || 'provided' in v)
        );

        if (isNewStructure) {
            // New database structure: { docType: { required: bool, provided: bool } }
            newStructureDocTypes.forEach(({ key, label }) => {
                const docProvision = provisions[key as keyof DocumentProvisions] as DocumentProvisionItem | undefined;
                if (docProvision?.required) {
                    requiredDocuments.push(label);
                    if (docProvision?.provided) {
                        uploadedDocuments.push(label);
                    }
                }
            });
        } else {
            // Legacy structure support
            const legacyDocTypes = [
                { key: 'fab_coa', label: 'Certificate of Analysis (CoA)' },
                { key: 'fab_coc', label: 'Certificate of Compliance (CoC)' },
                { key: 'fab_com', label: 'Certificate of Manufacture (CoM)' },
                { key: 'batch_certificate', label: 'Batch Certificate' },
                { key: 'executed_batch_record', label: 'Executed Batch Record' },
                { key: 'hc_group2', label: 'HC Group 2' },
                { key: 'hc_group3', label: 'HC Group 3' },
                { key: 'hc_group4', label: 'HC Group 4' },
            ];

            legacyDocTypes.forEach(({ key, label }) => {
                const statusKey = `${key}_status` as keyof DocumentProvisions;
                const docIdKey = `${key}_document_id` as keyof DocumentProvisions;
                const status = provisions[statusKey];
                const docId = provisions[docIdKey];

                if (status === 'Yes') {
                    requiredDocuments.push(label);
                    if (docId) {
                        uploadedDocuments.push(label);
                    }
                }
            });
        }

        const missingDocuments = requiredDocuments.filter((doc) => !uploadedDocuments.includes(doc));
        const completenessPercent = requiredDocuments.length > 0 
            ? Math.round((uploadedDocuments.length / requiredDocuments.length) * 100) 
            : 100;

        return {
            shipmentId: shipment.id,
            shipmentRef: shipment.shipmentRef,
            customerName: shipment.customerName,
            documents: [],
            documentProvisions: shipment.documentProvisions,
            requiredDocuments,
            uploadedDocuments,
            missingDocuments,
            completenessPercent,
        };
    }

    private arrayToCsv(data: string[][]): string {
        return data
            .map((row) =>
                row
                    .map((cell) => {
                        // Escape quotes and wrap in quotes if contains comma, quote, or newline
                        const escaped = cell.replace(/"/g, '""');
                        if (escaped.includes(',') || escaped.includes('"') || escaped.includes('\n')) {
                            return `"${escaped}"`;
                        }
                        return escaped;
                    })
                    .join(','),
            )
            .join('\n');
    }

    /**
     * Trigger download of CSV content
     */
    downloadCsv(csvContent: string, filename: string): void {
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(url);
    }
}




